import React, { useState, useMemo, useEffect } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import {
  ESSENTIALUPDATE,
  ESSENTAILADD,
  ESSENTIALDETAIL,
} from "../../../../services/ApiCalls";
import Loader from "../../../../components/Common/Loader";
import fileUploader from "../../../../utilities/fileUploader";
import Loading from "../../../../components/Common/Loading";
import { useTranslation } from "react-i18next";

const getSchema = (t) =>
  z.object({
    imgUrl: z.string().min(1, t("amenity.form.validation.imageRequired")),
    name: z.string({ required_error: t("amenity.form.validation.nameRequired") }),
    description: z.string().min(1, t("amenity.form.validation.descriptionRequired")),
    category: z.enum([
      "Common Areas",
      "In-room items",
     
    ], { required_error: t("amenity.form.validation.categoryRequired") }),
    status: z.enum(["Active", "Inactive"]).optional(),
  });

const AddAmenity = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;
  const [promoCodeDetails, setPromocodeDetails] = useState(null);
  const { id } = useParams();
  const schema = useMemo(() => getSchema(t), [t]);
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [imgloader, setImgLoader] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      imgUrl: "",
      name: "",
      description: "",
      status: "Active",
    },
  });

  const submitHandler = catchAsync(async (data) => {
    const body = {
      ...data,
      ...(id && { _id: id }),
      status: data.status,
    };

    const res = await (id ? ESSENTIALUPDATE(body) : ESSENTAILADD(body));
    const success = checkResponse({ res, showSuccess: true });

    if (success) {
      navigate("/amenities");
    }
  });

  const getData = catchAsync(async () => {
    setLoader(true);
    const res = await ESSENTIALDETAIL(id);
    const success = checkResponse({
      res,
      setData: setPromocodeDetails,
      setLoader,
    });
    if (success && res?.data?.data) {
      const fetched = res.data.data;
      reset({
        ...fetched,
        status:
          fetched.status?.toLowerCase() === "active" ? "Active" : "Inactive",
      });
      setImagePreview(fetched.imgUrl);
    }
  }, setLoader);

  useEffect(() => {
    if (id) getData();
  }, [id]);

  const handleCancel = () => {
    navigate(`/amenities?page=${page}`);
  };

  const handleImageChange = catchAsync(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImgLoader(true);
    const res = await fileUploader(file);
    setImgLoader(false);

    if (!res?.link) return;

    setValue("imgUrl", res.link, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  });

  // console.log(promoCodeDetails, "promoCodeDetails====<>");

  // console.log(errors, "errors========<>");
  return (
    <>
      {loader && <Loader />}
      <section className="addPromo position-relative py-3">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to={`/amenities?page=${page}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {id ? t("amenity.form.editTitle") : t("amenity.form.addTitle")}
                </h4>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row className="justify-content-between">
                    <Col lg="5" md="6" className="my-2">
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          {t("amenity.form.amenitiesImage")}
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={handleImageChange}
                        />
                        {errors?.imgUrl && (
                          <p className="text-danger m-0">
                            {errors?.imgUrl?.message}
                          </p>
                        )}
                        {imgloader ? (
                          <div
                            style={{ height: 140, width: 140 }}
                            className="img-fluid rounded-circle object-fit-cover"
                          >
                            <Loading fullSize={true} />
                          </div>
                        ) : (
                          imagePreview && (
                            <img
                              src={imagePreview}
                              alt="Preview"
                              style={{ width: "50%", marginTop: "10px" }}
                            />
                          )
                        )}
                      </div>
                    </Col>

                    <Col lg="5" md="6" className="my-2">
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          {t("amenity.form.amenitiesName")}
                        </label>
                        <input
                          type="text"
                          placeholder={t("amenity.form.amenityNamePlaceholder")}
                          className="form-control"
                          {...register("name")}
                        />
                        {errors?.name && (
                          <p className="text-danger m-0">
                            {errors?.name?.message}
                          </p>
                        )}
                      </div>
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          {t("amenity.form.status")}
                        </label>
                        <select
                          className="form-control"
                          {...register("status")}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                        {errors?.status && (
                          <p className="text-danger m-0">
                            {errors?.status?.message}
                          </p>
                        )}
                      </div>

                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          {t("amenity.form.description")}
                        </label>
                        <textarea
                          rows={5}
                          className="form-control h-auto"
                          {...register("description")}
                        ></textarea>
                        {errors?.description && (
                          <p className="text-danger m-0">
                            {errors?.description?.message}
                          </p>
                        )}
                      </div>
                      <div className="py-2">
  <label className="form-label fw-sbold text-muted ps-2 m-0">
    {t("amenity.form.category")}
  </label>
  <select
    className="form-control"
    {...register("category")}
    defaultValue=""
  >
    <option value="" disabled>
      {t("amenity.form.selectCategory")}
    </option>
    <option value="Common Areas">{t("amenity.form.commonAreas")}</option>
    <option value="In-room items">{t("amenity.form.inRoomItems")}</option>
  </select>
  {errors?.category && (
    <p className="text-danger m-0">{errors?.category?.message}</p>
  )}
</div>

                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-center gap-10">
                        <Button
                          type="button"
                          onClick={handleCancel}
                          className="d-flex align-items-center justify-content-center commonBtn GreyBtn"
                        >
                          {t("amenity.form.cancel")}
                        </Button>
                        <Button
                          type="submit"
                          className="d-flex align-items-center justify-content-center commonBtn "
                        >
                          {t("amenity.form.submit")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddAmenity;
